//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { useEffect, useRef, type MutableRefObject } from 'react';
import styled from '@emotion/styled';
import { CSSTransition } from 'react-transition-group';
import DataTableFilters from '@/components/_common/Filters/DataTableFilters';
import DataTableLayout from '@/components/_common/Layout/DataTableLayout';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setCurrentTable } from '@/redux/slices/dataTableSlice';
import { CALENDAR_PANELS_TRANSITION_DURATION, clearCache } from '@/redux/slices/focusCalendarSlice';
import { selectCurrentTableKey } from '@/redux/selectors/dataTableSelectors';
import { selectIsAvailExpanded, selectSidePanelTransitionState } from '@/redux/selectors/focusCalendarSelectors';
import AvailsCalendarTitleBar from './AvailsCalendarTitleBar';
import SidePanel from '@/components/_common/Table/Calendar/SidePanel/SidePanel';
import useResizeObserver from '@/components/_hooks/useResizeObserver';
import { selectIsNavBarVisible } from '@/redux/selectors/layoutSelectors';
import { NAVBAR_HEIGHT, NAVBAR_HIDE_TRANSITION_DURATION } from '@/constants/constants';

const AvailsCalendar = () => {
  const dispatch = useAppDispatch();
  const currentTableKey = useAppSelector(selectCurrentTableKey);
  const sidePanelTransitionState = useAppSelector(selectSidePanelTransitionState);
  const isNavBarVisible = useAppSelector(selectIsNavBarVisible);
  const isSidePanelVisible = sidePanelTransitionState !== 'exiting' && sidePanelTransitionState !== 'exited';
  const isAvailExpanded = useAppSelector(selectIsAvailExpanded);
  const wrapperElRef = useRef<HTMLDivElement | null>(null);
  const mainContentElRef = useRef<HTMLElement | null>(null);
  const sidePanelElRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    dispatch(setCurrentTable('focusCalendar'));

    return () => {
      dispatch(clearCache());
    };
  }, []);

  useWrapperHeightUpdater({
    wrapperElRef,
    mainContentElRef,
    sidePanelElRef
  });

  const wrapperClassName = isAvailExpanded ? 'sidePanelExpanded' : isSidePanelVisible ? 'sidePanelVisible' : 'sidePanelHidden';

  return (
    <>
      {currentTableKey === 'focusCalendar' && (
        <>
          <AvailsCalendarTitleBar />
          <Wrapper ref={wrapperElRef} className={wrapperClassName}>
            {/* The only reason why we need to use CSSTransition here is to utilize the `calendar-main-content-exit-done` class name */}
            <CSSTransition in={!isAvailExpanded} timeout={CALENDAR_PANELS_TRANSITION_DURATION} classNames="calendar-main-content">
              <MainContent ref={mainContentElRef}>
                <DataTableLayout tableKey="focusCalendar">
                  <DataTableFilters />
                </DataTableLayout>
              </MainContent>
            </CSSTransition>
            <SidePanelWrapper ref={sidePanelElRef} isNavBarVisible={isNavBarVisible}>
              <SidePanel />
            </SidePanelWrapper>
          </Wrapper>
        </>
      )}
    </>
  );
};

export default AvailsCalendar;

interface WrapperHeightUpdaterProps {
  wrapperElRef: MutableRefObject<HTMLDivElement | null>;
  mainContentElRef: MutableRefObject<HTMLElement | null>;
  sidePanelElRef: MutableRefObject<HTMLElement | null>;
}

// Since columns are positioned absolutely, we need to set the height of the wrapper to the max height of the columns
const useWrapperHeightUpdater = ({ wrapperElRef, mainContentElRef, sidePanelElRef }: WrapperHeightUpdaterProps) => {
  const colsHeightsRef = useRef<{ left: number; right: number }>({ left: 0, right: 0 });

  const setWrapperHeight = () => {
    if (!wrapperElRef.current) return;
    wrapperElRef.current.style.height = Math.max(colsHeightsRef.current.left, colsHeightsRef.current.right) + 'px';
  };

  useResizeObserver({
    ref: mainContentElRef,
    onResize: (entry: ResizeObserverEntry) => {
      colsHeightsRef.current.left = entry.contentRect.height;
      setWrapperHeight();
    }
  });

  useResizeObserver({
    ref: sidePanelElRef,
    onResize: (entry: ResizeObserverEntry) => {
      colsHeightsRef.current.right = entry.contentRect.height;
      setWrapperHeight();
    }
  });
};

const Wrapper = styled.div`
  position: relative;
  margin: 0 auto;

  ${({ theme }) => {
    const maxWidth = (theme as any).components?.MuiContainer?.styleOverrides?.root?.maxWidth; // taking the maxWidth from `globalBaseTheme`
    if (maxWidth) return `max-width: ${maxWidth}px;`;
  }}
`;

const sidePanelWidth = `480px`;
const mainContentReducedWidth = `calc(100% - ${sidePanelWidth} - 8px)`;
const transitionSettings = `${CALENDAR_PANELS_TRANSITION_DURATION}ms ease`;

const MainContent = styled.section`
  position: absolute;
  left: 0;
  width: 100%;
  opacity: 1;
  transition:
    width ${transitionSettings},
    transform ${transitionSettings},
    opacity ${transitionSettings};

  .sidePanelVisible > & {
    width: ${mainContentReducedWidth};
  }

  .sidePanelExpanded > & {
    width: ${mainContentReducedWidth};
    transform: translateX(-100%);
    opacity: 0;
  }

  // When MainContent is hidden, we set the height to 0 to make sure that it doesn't take any vertical space.
  // Otherwise if SidePanelWrapper's height is smaller than MainContent's height, there will be unwanted empty space below the SidePanelWrapper.
  &.calendar-main-content-exit-done {
    height: 0;
    overflow: hidden;
  }
`;

const SidePanelWrapper = styled.aside<{ isNavBarVisible: boolean }>`
  position: sticky;
  top: ${({ isNavBarVisible }) => (isNavBarVisible ? `${NAVBAR_HEIGHT}px` : 0)};
  margin-left: calc(100% - ${sidePanelWidth} - 24px);
  padding-top: 20px;

  width: ${sidePanelWidth};
  transform: translateX(100%);
  opacity: 0;

  transition:
    top ${NAVBAR_HIDE_TRANSITION_DURATION}ms ease,
    width ${transitionSettings},
    margin-left ${transitionSettings},
    opacity ${transitionSettings},
    transform ${transitionSettings};

  .sidePanelVisible > & {
    transform: translateX(0);
    opacity: 1;
  }

  .sidePanelExpanded > & {
    margin-left: 24px;
    width: calc(100% - 24px * 2);
    transform: translateX(0);
    opacity: 1;
  }
`;
